import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { ReverseEntryOptions } from '@/api/handlers/resources/journal/IJournalService';
import type { JournalEntryWriteResponse } from '@/api/handlers/resources/journal/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { OperationsQueryKeys } from '../operations/operationsQueryKeys';
import { JournalQueryKeys } from './journalQueryKeys';

/**
 * Reverse a posted entry — the only correction path for immutable posted
 * entries. Marks the original REVERSED and creates the balancing entry
 * (returned). Invalidates the resource so the list + the original's detail
 * refetch. Never rejects — branch on `result.success`.
 *
 * Takes the entry id plus the server's optional overrides, because an entry a
 * document produced needs `force` and a `reason` (bos-api BOS-135).
 */
export const useReverseJournalEntryMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<JournalEntryWriteResponse>,
        Error,
        { id: string; options?: ReverseEntryOptions }
    >({
        mutationFn: ({ id, options }) =>
            apiClient.journal.reverseEntry(id, options),
        onSuccess: (result) => {
            if (result.success) {
                queryClient.invalidateQueries({
                    queryKey: JournalQueryKeys.all,
                });
                // The entry just reversed may be a recorded operation's, and the
                // Operations Activity register renders its status chip and its
                // reversal linkage off exactly this. The invalidation lives here
                // rather than at the Activity tab's call site because the fact
                // that changed is the journal's, and any caller of this mutation
                // changes it.
                queryClient.invalidateQueries({
                    queryKey: OperationsQueryKeys.all,
                });
            }
        },
    });
};
